import { useState, useEffect, useCallback } from 'react';
import { X } from 'lucide-react';

const OPERATORS = ['+', '-', '*', '/'];

function CalculatorModal({ isOpen, onClose, initialValue = '', onResult }) {
  const [expression, setExpression] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      const normalizedValue = initialValue ? String(initialValue).replace(/,/g, '.') : '';
      setExpression(normalizedValue);
      setError('');
    }
  }, [initialValue, isOpen]);

  const appendValue = useCallback((value) => {
    setError('');
    setExpression((prev) => {
      if (OPERATORS.includes(value)) {
        if (!prev) {
          return value === '-' ? value : prev;
        }

        if (OPERATORS.includes(prev.slice(-1))) {
          return prev.slice(0, -1) + value;
        }
      }

      if (value === '.') {
        const parts = prev.split(/[+\-*/]/);
        const currentNumber = parts[parts.length - 1];
        if (currentNumber.includes('.')) {
          return prev;
        }
      }

      return prev + value;
    });
  }, []);

  const handleBackspace = useCallback(() => {
    setError('');
    setExpression((prev) => prev.slice(0, -1));
  }, []);

  const handleClear = useCallback(() => {
    setError('');
    setExpression('');
  }, []);

  const handleEquals = useCallback(() => {
    if (!expression) {
      setError('Inserisci un valore.');
      return;
    }

    try {
      const sanitized = expression.replace(/[^0-9+\-*/.]/g, '');

      if (!sanitized) {
        setError('Calcolo non valido.');
        return;
      }

      const lastChar = sanitized.slice(-1);
      if (OPERATORS.includes(lastChar)) {
        setError('Completa il calcolo prima di confermare.');
        return;
      }

      const result = Function('"use strict"; return (' + sanitized + ');')();

      if (!Number.isFinite(result)) {
        setError('Risultato non valido.');
        return;
      }

      const rounded = Math.round(result * 100) / 100;
      const formatted = rounded.toFixed(2);
      onResult(formatted);
    } catch {
      setError('Calcolo non valido.');
    }
  }, [expression, onResult]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }

      if (event.key === 'Enter') {
        event.preventDefault();
        handleEquals();
      }

      if (/[0-9]/.test(event.key)) {
        event.preventDefault();
        appendValue(event.key);
      }

      if (event.key === '.') {
        event.preventDefault();
        appendValue('.');
      }

      if (['+', '-', '*', '/'].includes(event.key)) {
        event.preventDefault();
        appendValue(event.key);
      }

      if (event.key === 'Backspace') {
        event.preventDefault();
        handleBackspace();
      }

      if (event.key === 'Delete') {
        event.preventDefault();
        handleClear();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [appendValue, handleBackspace, handleClear, handleEquals, isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  const keypad = [
    ['7', '8', '9', '/'],
    ['4', '5', '6', '*'],
    ['1', '2', '3', '-'],
    ['0', '.', '⌫', '+']
  ];

  const displayExpression = expression.replace(/\*/g, '×');

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-xs rounded-3xl bg-white p-5 shadow-xl dark:bg-gray-900 dark:text-gray-100"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">Calcolatrice</h2>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-gray-100 text-gray-500 transition-colors hover:bg-gray-200 hover:text-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-gray-100"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="mb-3 rounded-2xl border border-gray-200 bg-gray-50 p-4 text-right text-2xl font-semibold tracking-wide dark:border-gray-700 dark:bg-gray-800">
          {displayExpression || '0'}
        </div>

        {error && (
          <p className="mb-3 text-sm text-red-500">{error}</p>
        )}

        <div className="grid grid-cols-4 gap-2">
          {keypad.flat().map((value) => {
            if (value === '⌫') {
              return (
                <button
                  key={value}
                  type="button"
                  onClick={handleBackspace}
                  className="h-12 rounded-2xl bg-gray-100 text-lg font-semibold transition-colors hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700"
                >
                  {value}
                </button>
              );
            }

            if (value === '.') {
              return (
                <button
                  key={value}
                  type="button"
                  onClick={() => appendValue(value)}
                  className="h-12 rounded-2xl bg-gray-100 text-lg font-semibold transition-colors hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700"
                >
                  {value}
                </button>
              );
            }

            if (value === '/') {
              return (
                <button
                  key={value}
                  type="button"
                  onClick={() => appendValue(value)}
                  className="h-12 rounded-2xl bg-blue-100 text-lg font-semibold text-blue-600 transition-colors hover:bg-blue-200 dark:bg-blue-900/40 dark:text-blue-200 dark:hover:bg-blue-900/60"
                >
                  {value}
                </button>
              );
            }

            if (value === '*') {
              return (
                <button
                  key={value}
                  type="button"
                  onClick={() => appendValue(value)}
                  className="h-12 rounded-2xl bg-blue-100 text-lg font-semibold text-blue-600 transition-colors hover:bg-blue-200 dark:bg-blue-900/40 dark:text-blue-200 dark:hover:bg-blue-900/60"
                >
                  ×
                </button>
              );
            }

            if (value === '-') {
              return (
                <button
                  key={value}
                  type="button"
                  onClick={() => appendValue(value)}
                  className="h-12 rounded-2xl bg-blue-100 text-lg font-semibold text-blue-600 transition-colors hover:bg-blue-200 dark:bg-blue-900/40 dark:text-blue-200 dark:hover:bg-blue-900/60"
                >
                  {value}
                </button>
              );
            }

            if (value === '+') {
              return (
                <button
                  key={value}
                  type="button"
                  onClick={() => appendValue(value)}
                  className="h-12 rounded-2xl bg-blue-100 text-lg font-semibold text-blue-600 transition-colors hover:bg-blue-200 dark:bg-blue-900/40 dark:text-blue-200 dark:hover:bg-blue-900/60"
                >
                  {value}
                </button>
              );
            }

            return (
              <button
                key={value}
                type="button"
                onClick={() => appendValue(value)}
                className="h-12 rounded-2xl bg-gray-100 text-lg font-semibold transition-colors hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700"
              >
                {value}
              </button>
            );
          })}
        </div>

        <div className="mt-3 grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={handleClear}
            className="h-12 rounded-2xl bg-gray-200 text-base font-semibold transition-colors hover:bg-gray-300 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700"
          >
            Cancella
          </button>
          <button
            type="button"
            onClick={handleEquals}
            className="h-12 rounded-2xl bg-blue-600 text-base font-semibold text-white transition-colors hover:bg-blue-700"
          >
            =
          </button>
        </div>
      </div>
    </div>
  );
}

export default CalculatorModal;
